import { useEffect, useState, useMemo, useCallback } from "react";

import {
    Checkbox,
    Column,
    DialogWrapper,
    MAX_DIALOG_HEIGHT,
    OutlinedOption,
    Row,
    SearchInput,
    SkeletonRounded,
    StaticGrid,
    Text,
    TextButton,
    ToggleSpacedButtons,
    VerticalScroll,
    repeatElement,
    useAppPalette
} from "@bridgesplit/ui";
import {
    BsMetaUtil,
    getSearchFromMetadata,
    useCollateralWithTerms,
    getDefaultMarketInfo
} from "@bridgesplit/abf-react";
import { MISSING_PARAM_ERROR, Result, deepCompareSets, formatPercentArray, textContains } from "@bridgesplit/utils";
import { Divider } from "@mui/material";
import { DispatchType } from "@bridgesplit/react";
import { AppDialog } from "app/utils";

import MarketDialogHeader from "./common";
import { useCollateralTokensByTag, useMarketDialog } from "../util";
import { AppButton, OverlappingMetadataImages, TokenImage } from "../../common";
import { RewardsCollateralLabel } from "../../points";
import { createExtendedCollateralEntries, ExtendedCollateralEntry } from "./utils";

export default function MultiSelectCollateralDialog() {
    return (
        <DialogWrapper>
            <MultiSelectCollateralInternal />
        </DialogWrapper>
    );
}

enum Mode {
    Auto = "Categories",
    Advanced = "Custom"
}

export function MultiSelectCollateralInternal() {
    const { close, getData } = useMarketDialog();

    const data = getData(AppDialog.MultiSelectCollateral);

    // Track selections per market info to preserve selections when switching
    const [selectionsByMarketInfo, setSelectionsByMarketInfo] = useState<Map<string, Set<string>>>(new Map());
    const [modeState, setMode] = useState<Mode>();
    const defaultMode = data?.isAdvanced ? Mode.Advanced : Mode.Auto;
    const mode = modeState === undefined ? defaultMode : modeState;

    // Market info selection state for creating strategies with multiple verified market infos
    const [selectedMarketInfo, setSelectedMarketInfo] = useState<string | undefined>(data?.selectedMarketInfoAddress);

    // Determine if we should show the market info selector
    // Show only when: creating a strategy (no existingStrategy, no customMarketInformation) with 2+ verified market infos
    const marketInfoOptions = useMemo(() => {
        if (!data?.verifiedMarketInfos || data.verifiedMarketInfos.length <= 1) return undefined;
        if (data?.existingStrategy || data?.customMarketInformation) return undefined;
        return data.verifiedMarketInfos.map((mi) => ({
            address: mi.marketInformation.address,
            label: mi.marketInformation.name
        }));
    }, [data?.verifiedMarketInfos, data?.existingStrategy, data?.customMarketInformation]);

    // Initialize selectedMarketInfo to the "Default" option if not already set
    // Only needed when market info selector is shown
    useEffect(() => {
        if (marketInfoOptions && !selectedMarketInfo && data?.verifiedMarketInfos) {
            const defaultMarketInfo = getDefaultMarketInfo(data.verifiedMarketInfos);
            setSelectedMarketInfo(defaultMarketInfo?.marketInformation.address || marketInfoOptions[0]?.address);
        }
    }, [marketInfoOptions, selectedMarketInfo, data?.verifiedMarketInfos]);

    // Pre-fetch collateral for ALL market infos to avoid refetching when switching
    const { data: collateral, isLoading: collateralLoading } = useCollateralWithTerms({
        principalMint: data?.principalMint,
        selectedMarketInfoAddress: marketInfoOptions ? undefined : selectedMarketInfo, // Don't filter if multiple options
        collateral: data?.tokens,
        marketInformationAddresses: marketInfoOptions?.map((marketInfo) => marketInfo.address),
        existingStrategy: data?.existingStrategy
    });

    // Derive current selection from the map based on selected market info
    // For cases without market info selector (existing strategy), use a constant key "all"
    const selectionKey = selectedMarketInfo || "all";
    const selected = useMemo(() => {
        return selectionsByMarketInfo.get(selectionKey) || new Set<string>();
    }, [selectionsByMarketInfo, selectionKey]);

    // Helper to update selection for current market info
    const setSelected = useCallback(
        (updater: Set<string> | undefined | ((prev: Set<string> | undefined) => Set<string> | undefined)) => {
            setSelectionsByMarketInfo((prev) => {
                const copy = new Map(prev);
                const currentSelection = copy.get(selectionKey) || new Set<string>();
                const newSelection = typeof updater === "function" ? updater(currentSelection) : updater;
                if (newSelection) {
                    copy.set(selectionKey, newSelection);
                } else {
                    copy.delete(selectionKey);
                }
                return copy;
            });
        },
        [selectionKey]
    );

    // Create extended collateral entries with oracle differentiation
    const extendedCollateralEntries = useMemo(
        () =>
            createExtendedCollateralEntries(
                data?.tokens,
                data?.collateralToMarketInfoOptions,
                data?.existingStrategyCollaterals,
                data?.enableMultiOracleSelection
            ),
        [
            data?.tokens,
            data?.collateralToMarketInfoOptions,
            data?.existingStrategyCollaterals,
            data?.enableMultiOracleSelection
        ]
    );

    // Filter extended entries based on selected market info
    const filteredExtendedEntries = useMemo(() => {
        // If we have verified market infos, filter to only show collateral in the selected/default market info
        if (data?.verifiedMarketInfos && data.verifiedMarketInfos.length > 0) {
            // Determine which market info to use for filtering
            let marketInfoToUse;

            if (marketInfoOptions && selectedMarketInfo) {
                // Multiple market infos: use the selected one
                marketInfoToUse = data.verifiedMarketInfos.find(
                    (mi) => mi.marketInformation.address === selectedMarketInfo
                );
            } else if (data.verifiedMarketInfos.length === 1) {
                // Single market info: use it directly
                marketInfoToUse = data.verifiedMarketInfos[0];
            }

            if (marketInfoToUse) {
                const collateralMintsInMarketInfo = new Set(
                    marketInfoToUse.marketInformationAssetInfo.map((ltv) => ltv.assetIdentifier)
                );

                // Only show entries whose mint is in this market info
                return extendedCollateralEntries.filter((entry) => collateralMintsInMarketInfo.has(entry.mint));
            }
        }

        return extendedCollateralEntries;
    }, [extendedCollateralEntries, marketInfoOptions, selectedMarketInfo, data?.verifiedMarketInfos]);

    // Create a map from mint to its extended entries (for multi-oracle handling)
    // Use filtered entries so we only see collateral in the selected market info
    const mintToExtendedEntries = useMemo(() => {
        const map = new Map<string, ExtendedCollateralEntry[]>();
        for (const entry of filteredExtendedEntries) {
            const existing = map.get(entry.mint) || [];
            existing.push(entry);
            map.set(entry.mint, existing);
        }
        return map;
    }, [filteredExtendedEntries]);

    // Build displayCollateral from filtered extended entries when we have market info options
    // This ensures we show ALL collateral in the selected market info, even if useCollateralWithTerms
    // doesn't return terms for some of them
    const displayCollateral = useMemo(() => {
        if (!marketInfoOptions && !data?.collateralToMarketInfoOptions) {
            // No market info options - use collateral from useCollateralWithTerms directly
            return collateral;
        }

        // Build from filtered extended entries since they already have all the info we need
        // Deduplicate by mint (one entry per token for display purposes)
        const mintToEntry = new Map<string, ExtendedCollateralEntry>();
        for (const entry of filteredExtendedEntries) {
            if (!mintToEntry.has(entry.mint)) {
                mintToEntry.set(entry.mint, entry);
            }
        }

        // Convert to CollateralWithTerms-like format
        // The rendering code will use entry.ltv from extended entries, so we just need metadata
        return Array.from(mintToEntry.values()).map((entry) => ({
            metadata: entry.metadata,
            ltvInfo: {
                ltv: entry.ltv,
                liquidationThreshold: entry.liquidationThreshold
            }
        }));
    }, [collateral, marketInfoOptions, filteredExtendedEntries, data?.collateralToMarketInfoOptions]);

    // Show loading collateral by tag while fetching
    const collateralByTag = useCollateralTokensByTag(collateralLoading ? undefined : displayCollateral);

    const marketInfoCollateralMints = displayCollateral?.map((c) => c.metadata.mint);

    const supportedCollateral = data?.collateral?.filter((c) => marketInfoCollateralMints?.includes(c));

    // Initialize selections map with keys from supportedCollateral
    useEffect(() => {
        if (selectionsByMarketInfo.size === 0 && supportedCollateral && mintToExtendedEntries.size > 0) {
            // Map supported mints to their corresponding keys in extended entries
            const initialSelectedKeys = new Set<string>();
            for (const mint of supportedCollateral) {
                const entries = mintToExtendedEntries.get(mint);
                if (!entries) continue;

                if (entries.length === 1) {
                    // Single oracle: use the entry's key
                    initialSelectedKeys.add(entries[0].key);
                } else if (entries.length > 1) {
                    // Multiple oracles: check if we have existing selection info
                    const existingSelection = data?.existingCollateralSelections?.[mint];

                    if (existingSelection?.oracleAddress) {
                        // Find the entry that matches the existing oracle address
                        const matchingEntry = entries.find(
                            (entry) => entry.oracleInfo?.address === existingSelection.oracleAddress
                        );

                        if (matchingEntry) {
                            initialSelectedKeys.add(matchingEntry.key);
                        }
                        // If no match found, don't pre-select any (user must choose)
                    }
                    // If no existing selection, don't pre-select any (user must choose)
                }
            }
            // Initialize the map with the selection key (either selected market info or "all")
            setSelectionsByMarketInfo(new Map([[selectionKey, initialSelectedKeys]]));
        }
    }, [
        supportedCollateral,
        selectionsByMarketInfo,
        mintToExtendedEntries,
        data?.existingCollateralSelections,
        selectionKey
    ]);

    const { tokensByTags } = collateralByTag;

    const props = {
        collateralByTag,
        selected,
        setSelected,
        extendedCollateralEntries: filteredExtendedEntries,
        mintToExtendedEntries
    };

    function submit() {
        if (!displayCollateral || !data?.setCollateral) return;

        // Map selected keys back to collateral using current selection
        // For multi-oracle collateral, we need to ensure the correct market info is selected
        const selectedCollateral = displayCollateral.filter((c) => {
            const entries = mintToExtendedEntries.get(c.metadata.mint);
            if (!entries) return false;

            // Check if any of this mint's extended entries are selected in current market info
            return entries.some((entry) => selected.has(entry.key));
        });

        // Build a map of mint -> selected market info for collateral with multiple oracles
        // This will be used by the setCollateral handler to set the correct oracle
        const mintToSelectedMarketInfo = new Map<
            string,
            { marketInfoAddress: string; oracleAddress: string; oracleType: number }
        >();
        for (const entry of filteredExtendedEntries) {
            if (selected.has(entry.key) && entry.oracleInfo) {
                mintToSelectedMarketInfo.set(entry.mint, {
                    marketInfoAddress: entry.marketInformationAddress,
                    oracleAddress: entry.oracleInfo.address,
                    oracleType: entry.oracleInfo.type
                });
            }
        }

        // Store the selection info in the collateral objects as a custom property
        // This is a bit of a hack but allows us to pass the info through the existing callback
        const collateralWithSelection = selectedCollateral.map((c) => {
            const selectionInfo = mintToSelectedMarketInfo.get(c.metadata.mint);
            return selectionInfo ? { ...c, __selectedOracleInfo: selectionInfo } : c;
        });

        data?.setCollateral?.(collateralWithSelection, selectedMarketInfo);
        close();
    }

    async function onClickWithResult() {
        if (!displayCollateral || !data?.setCollateralAsync) return Result.errFromMessage(MISSING_PARAM_ERROR);

        // Map selected keys back to collateral using current selection
        const selectedCollateral = displayCollateral.filter((c) => {
            const entries = mintToExtendedEntries.get(c.metadata.mint);
            if (!entries) return false;

            // Check if any of this mint's extended entries are selected in current market info
            return entries.some((entry) => selected.has(entry.key));
        });

        // Build a map of mint -> selected market info for collateral with multiple oracles
        const mintToSelectedMarketInfo = new Map<
            string,
            { marketInfoAddress: string; oracleAddress: string; oracleType: number }
        >();
        for (const entry of filteredExtendedEntries) {
            if (selected.has(entry.key) && entry.oracleInfo) {
                mintToSelectedMarketInfo.set(entry.mint, {
                    marketInfoAddress: entry.marketInformationAddress,
                    oracleAddress: entry.oracleInfo.address,
                    oracleType: entry.oracleInfo.type
                });
            }
        }

        // Store the selection info in the collateral objects as a custom property
        const collateralWithSelection = selectedCollateral.map((c) => {
            const selectionInfo = mintToSelectedMarketInfo.get(c.metadata.mint);
            return selectionInfo ? { ...c, __selectedOracleInfo: selectionInfo } : c;
        });

        const result = await data.setCollateralAsync(collateralWithSelection as any);

        if (result.isOk()) {
            close();
        }
        return result;
    }

    // Check if changes were made - compare keys instead of mints
    const initialSelectedKeys = useMemo(() => {
        const keys = new Set<string>();
        for (const mint of data?.collateral || []) {
            const entries = mintToExtendedEntries.get(mint);
            if (!entries) continue;

            if (entries.length === 1) {
                keys.add(entries[0].key);
            } else if (entries.length > 1) {
                // Multiple oracles: check if we have existing selection info
                const existingSelection = data?.existingCollateralSelections?.[mint];

                if (existingSelection?.oracleAddress) {
                    // Find the entry that matches the existing oracle address
                    const matchingEntry = entries.find(
                        (entry) => entry.oracleInfo?.address === existingSelection.oracleAddress
                    );

                    if (matchingEntry) {
                        keys.add(matchingEntry.key);
                    }
                }
            }
        }
        return keys;
    }, [data?.collateral, mintToExtendedEntries, data?.existingCollateralSelections]);

    const changesMade = !deepCompareSets(selected, initialSelectedKeys);

    return (
        <>
            {marketInfoOptions ? (
                <>
                    <MarketDialogHeader header="Select Market" />
                    <ToggleSpacedButtons
                        size="small"
                        value={selectedMarketInfo}
                        setValue={(newValue) => {
                            setSelectedMarketInfo(newValue);
                        }}
                        options={marketInfoOptions.map((opt) => ({ value: opt.address, label: opt.label }))}
                    />
                    <Divider />
                    <MarketDialogHeader header={data?.header ?? "Select collateral"} />
                </>
            ) : (
                <MarketDialogHeader header={data?.header ?? "Select collateral"} />
            )}

            <ToggleSpacedButtons
                size="small"
                value={mode}
                setValue={setMode}
                options={Object.values(Mode).map((value) => ({ value }))}
            />

            {mode === Mode.Advanced ? <CustomSelection {...props} /> : <AutoSelection {...props} />}
            <Column spacing={1}>
                <Row spaceBetween>
                    <Text color="caption">
                        {selected.size} token{selected.size === 1 ? "" : "s"} selected
                    </Text>
                    <Row spacing={1}>
                        <TextButton color="caption" onClick={() => setSelected(new Set())}>
                            Clear
                        </TextButton>
                        <TextButton
                            color="secondary"
                            onClick={() => {
                                // Select first entry for each collateral that has valid terms (in current market info)
                                const allKeys = new Set<string>();
                                const validMints = new Set(displayCollateral?.map((c) => c.metadata.mint));
                                for (const [mint, entries] of mintToExtendedEntries.entries()) {
                                    // Only select if this mint has valid collateral terms
                                    if (validMints.has(mint) && entries.length > 0) {
                                        allKeys.add(entries[0].key);
                                    }
                                }
                                setSelected(allKeys);
                            }}
                        >
                            All
                        </TextButton>
                    </Row>
                </Row>
                {!tokensByTags && <Column spacing={1}> {repeatElement(<SkeletonRounded height="20px" />, 3)}</Column>}

                <AppButton
                    isTransaction={false}
                    asyncCta={
                        data?.setCollateralAsync
                            ? {
                                  onClickWithResult: onClickWithResult
                              }
                            : undefined
                    }
                    disabled={!changesMade || !selected.size}
                    variant="contained"
                    onClick={submit}
                >
                    {(() => {
                        if (!changesMade) return "No changes made";
                        if (!selected.size) return "Select collateral";
                        return "Confirm selection";
                    })()}
                </AppButton>
            </Column>
        </>
    );
}

type CollateralByTagResponse = ReturnType<typeof useCollateralTokensByTag>;

type TokenSelectProps = {
    collateralByTag: CollateralByTagResponse;
    selected: Set<string>;
    setSelected: DispatchType<Set<string> | undefined>;
    extendedCollateralEntries: ExtendedCollateralEntry[];
    mintToExtendedEntries: Map<string, ExtendedCollateralEntry[]>;
};

function CustomSelection({
    collateralByTag: { tokensByTags },
    selected,
    setSelected,
    extendedCollateralEntries,
    mintToExtendedEntries
}: TokenSelectProps) {
    const { hoverBackground } = useAppPalette();
    const [search, setSearch] = useState("");

    // Helper function to handle selection with mutual exclusion
    const handleToggleSelection = (entry: ExtendedCollateralEntry) => {
        setSelected((prev) => {
            const dupe = new Set(prev);
            const isCurrentlySelected = dupe.has(entry.key);

            if (isCurrentlySelected) {
                // Deselect this entry
                dupe.delete(entry.key);
            } else {
                // Select this entry and deselect any other entries for the same mint
                const entries = mintToExtendedEntries.get(entry.mint);
                if (entries && entries.length > 1) {
                    // Remove all other entries for this mint (mutual exclusion)
                    entries.forEach((e) => {
                        if (e.key !== entry.key) {
                            dupe.delete(e.key);
                        }
                    });
                }
                dupe.add(entry.key);
            }

            return dupe;
        });
    };

    const filtered = tokensByTags
        ?.map(({ tag, tokens }) => {
            const filteredTokens = tokens.filter(({ metadata }) => {
                if (metadata.mint === search) return true;
                return textContains(getSearchFromMetadata(metadata), search);
            });

            return { tag, tokens: filteredTokens };
        })
        .filter(({ tokens }) => tokens.length);

    return (
        <>
            <SearchInput
                placeholder="Search by token or address"
                onChange={(e) => setSearch(e.target.value)}
                value={search}
            />
            <VerticalScroll sx={{ mx: -2 }} spacing={2} maxHeight={MAX_DIALOG_HEIGHT}>
                {filtered?.map(({ tokens, tag }, i) => {
                    // Count selected based on keys, not mints
                    const selectedCount = tokens.filter((t) => {
                        const entries = mintToExtendedEntries.get(t.metadata.mint);
                        return entries?.some((e) => selected.has(e.key));
                    }).length;
                    const allSelected = selectedCount === tokens.length;

                    return (
                        <Column key={i} spacing={1}>
                            <Row sx={{ px: 2 }} spaceBetween>
                                <Text color="caption">{tag.label}</Text>
                                <Checkbox
                                    indeterminate={!allSelected && !!selectedCount}
                                    checked={allSelected}
                                    onChange={() => {
                                        setSelected((prev) => {
                                            const dupe = new Set(prev);
                                            const shouldSelect = selectedCount === 0;

                                            for (const token of tokens) {
                                                const entries = mintToExtendedEntries.get(token.metadata.mint);
                                                if (!entries) continue;

                                                if (shouldSelect) {
                                                    // Select first entry for each mint
                                                    dupe.add(entries[0].key);
                                                } else {
                                                    // Deselect all entries for this mint
                                                    entries.forEach((e) => dupe.delete(e.key));
                                                }
                                            }

                                            return dupe;
                                        });
                                    }}
                                />
                            </Row>

                            <Divider />
                            <Column>
                                {tokens.map(({ metadata, ...terms }) => {
                                    const entries = mintToExtendedEntries.get(metadata.mint);
                                    if (!entries) return null;

                                    // Render each extended entry (may be multiple for same collateral)
                                    return entries.map((entry) => {
                                        const ltvFlat = "ltvInfo" in terms ? [terms.ltvInfo.ltv] : [entry.ltv];
                                        const isSelected = selected.has(entry.key);

                                        return (
                                            <Row
                                                key={entry.key}
                                                onClick={() => handleToggleSelection(entry)}
                                                spaceBetween
                                                sx={{
                                                    px: 2,
                                                    py: 0.5,
                                                    cursor: "pointer",
                                                    ":hover": { background: hoverBackground }
                                                }}
                                            >
                                                <Row spacing={1}>
                                                    <TokenImage metadata={metadata} size="md" />

                                                    <Column>
                                                        <Row spacing={1}>
                                                            <Text>
                                                                {BsMetaUtil.getSymbolUnique(metadata)}
                                                                {entry.oracleInfo && ` (${entry.oracleInfo.name})`}
                                                            </Text>
                                                            <RewardsCollateralLabel metadata={metadata} />
                                                        </Row>
                                                        <Text variant="body2" color="caption">
                                                            {formatPercentArray(ltvFlat)} LTV
                                                        </Text>
                                                    </Column>
                                                </Row>
                                                <Checkbox checked={isSelected} />
                                            </Row>
                                        );
                                    });
                                })}
                            </Column>
                        </Column>
                    );
                })}
            </VerticalScroll>
        </>
    );
}

function AutoSelection({
    collateralByTag: { tokensByTags },
    selected,
    setSelected,
    mintToExtendedEntries
}: TokenSelectProps) {
    return (
        <StaticGrid gridGap={1} mobileColumns={2} columns={2}>
            {tokensByTags?.map(({ tag: { tag, shortLabel }, tokens }) => {
                // Check if all tokens in this category have at least one entry selected
                const isSelected = tokens.every((t) => {
                    const entries = mintToExtendedEntries.get(t.metadata.mint);
                    return entries?.some((e) => selected.has(e.key));
                });

                function onClick() {
                    setSelected((prev) => {
                        const copy = new Set(prev);

                        for (const token of tokens) {
                            const entries = mintToExtendedEntries.get(token.metadata.mint);
                            if (!entries) continue;

                            const hasAnySelected = entries.some((e) => copy.has(e.key));

                            if (hasAnySelected) {
                                // Deselect all entries for this token
                                entries.forEach((e) => copy.delete(e.key));
                            } else {
                                // Select first entry for this token
                                copy.add(entries[0].key);
                            }
                        }

                        return copy;
                    });
                }

                return (
                    <OutlinedOption onClick={onClick} padding={0} isSelected={isSelected} key={tag}>
                        <Column spacing={1} p={1}>
                            {tag && <Text color={isSelected ? "body" : "caption"}>{shortLabel}</Text>}

                            <OverlappingMetadataImages
                                maxLength={3}
                                plusVariant="caption"
                                size="20px"
                                metadata={tokens.map((t) => t.metadata)}
                            />
                        </Column>
                    </OutlinedOption>
                );
            })}
        </StaticGrid>
    );
}
